import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getMemberInvolvements } from '../api';
import { useAuth } from '../context/AuthContext';

export default function MemberInvolvementsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const involvements = await getMemberInvolvements(id, { pageSize: 100 });
        setData(involvements);
      } catch (err) {
        console.error(err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const involvementsRaw = data?.data;
  const member = data?.member;
  const eformExpiresYears = data?.eform_expires_after_years ?? 3;

  const formCompletionInfo = useMemo(() => {
    if (!member?.form_completed_at) return null;
    const completedDate = new Date(member.form_completed_at);
    const now = new Date();
    const yearsAgo = (now - completedDate) / (365.25 * 24 * 60 * 60 * 1000);
    const expiresYears = eformExpiresYears ?? 3;
    const isStale = yearsAgo >= expiresYears;
    const isOutOfDate = yearsAgo >= 1 && !isStale;
    const formattedDate = completedDate.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
    return {
      text: `Last time the member filled out the form was on ${formattedDate}.`,
      isStale,
      isOutOfDate,
    };
  }, [member?.form_completed_at, eformExpiresYears]);
  const involvements = useMemo(() => {
    const list = involvementsRaw ?? [];
    const byMinistry = {};
    list.forEach((inv) => {
      const ministryName = inv.ministry?.name ?? 'Unknown';
      if (!byMinistry[ministryName]) byMinistry[ministryName] = { ministry: ministryName, ministryAreas: [] };
      const ministryArea = inv.ministry_area;
      if (ministryArea && !byMinistry[ministryName].ministryAreas.some((m) => m.id === ministryArea.id)) {
        byMinistry[ministryName].ministryAreas.push({
          id: ministryArea.id,
          name: ministryArea.name,
          is_in_rotation: inv.is_in_rotation !== false,
        });
      }
    });
    return Object.values(byMinistry);
  }, [involvementsRaw]);

  if (loading || !data) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/members')}>
          Back to Members
        </Button>
        <Typography sx={{ mt: 2 }}>{loading ? 'Loading...' : 'Member not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        className="no-print"
        sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/members')}>
          Back to Members
        </Button>
        {isAdmin && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/members/${id}/preferences/edit`)}
          >
            Edit Involvements
          </Button>
        )}
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
          {formCompletionInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {formCompletionInfo.text}
              </Typography>
              {formCompletionInfo.isStale && <Chip label="Stale" color="error" size="small" />}
              {formCompletionInfo.isOutOfDate && <Chip label="Out-of-date" color="warning" size="small" />}
            </Box>
          )}
        </Box>
      </Box>

      <style>
        {`@media print {
          body * { visibility: hidden; }
          #member-involvements-print,
          #member-involvements-print * { visibility: visible; }
          #member-involvements-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print, .no-print * { display: none !important; }
          @page {
            size: 8.5in 11in;
            margin: 0.5in;
          }
          #member-involvements-print .print-grid {
            column-count: 3 !important;
            column-gap: 16pt !important;
            column-fill: auto !important;
          }
          #member-involvements-print .print-card {
            break-inside: avoid;
            margin-bottom: 8pt !important;
          }
          #member-involvements-print .MuiCard-root {
            box-shadow: none;
            border: 1px solid #000;
            padding: 6pt !important;
          }
          #member-involvements-print .MuiCardContent-root {
            padding: 4pt 8pt !important;
          }
          #member-involvements-print .MuiTypography-h5,
          #member-involvements-print .MuiTypography-h6 {
            color: #000 !important;
          }
          #member-involvements-print .MuiTypography-colorPrimary {
            color: #000 !important;
          }
          #member-involvements-print .MuiTypography-h6 {
            margin-bottom: 2pt !important;
            font-size: 0.9rem !important;
          }
          #member-involvements-print .MuiListItem-root {
            min-height: unset !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          #member-involvements-print .MuiSvgIcon-root[data-in-rotation="true"] {
            color: #2e7d32 !important;
          }
          #member-involvements-print .MuiSvgIcon-root[data-in-rotation="false"] {
            color: #d32f2f !important;
          }
        }`}
      </style>

      <Box id="member-involvements-print">
        <Typography variant="h5" sx={{ mt: 0, mb: 1 }}>
          Preferences for {member.first_name} {member.last_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {[
            member.email,
            member.phone &&
              member.phone.replace(/\D/g, '').replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3'),
            member.dob &&
              `Birthday ${new Date(member.dob).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>

        {involvements.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No involvements recorded.</Typography>
        </Paper>
      ) : (
        <Box
          className="print-grid"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {involvements.map((group) => (
            <Card key={group.ministry} className="print-card">
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  {group.ministry}
                </Typography>
                <List dense disablePadding>
                  {group.ministryAreas.map((ministryArea) => (
                    <ListItem key={ministryArea.id} sx={{ py: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {ministryArea.is_in_rotation ? (
                        <Tooltip title="The member is in the rotation for this ministry area.">
                          <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} data-in-rotation="true" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="This member is NOT in the rotation for this ministry area.">
                          <CloseIcon sx={{ color: 'error.main', fontSize: 18 }} data-in-rotation="false" />
                        </Tooltip>
                      )}
                      <ListItemText
                        primary={ministryArea.name}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      </Box>
    </Box>
  );
}
